import {
  AiStatusDto,
  ChartRequestDto,
  GeneratedChartDto,
  GeneratedImageDto,
  ImageRequestDto,
  RunDto,
  StartRunDto,
  TopicDto,
} from "@growth/dto";
import { useMutation, useQuery } from "@tanstack/react-query";

import { api } from "./api";

/** Generation calls can take minutes; don't let axios give up first. */
const LONG = { timeout: 10 * 60 * 1000 };

export const useAiStatus = () =>
  useQuery({
    queryKey: ["ai", "status"],
    queryFn: async () => (await api.get<AiStatusDto>("/ai/status")).data,
    staleTime: 5 * 60 * 1000,
  });

export const useSuggestTopics = () =>
  useMutation({
    mutationFn: async () =>
      (await api.post<{ topics: TopicDto[]; costUsd: number }>("/ai/topics", {}, LONG)).data,
  });

export const useStartRun = () =>
  useMutation({
    mutationFn: async (input: StartRunDto) =>
      (await api.post<{ id: string }>("/ai/runs", input)).data,
  });

/** Polls every 2 s while the run is in progress. */
export const useRun = (id: string) =>
  useQuery({
    queryKey: ["ai", "run", id],
    queryFn: async () => (await api.get<RunDto>(`/ai/runs/${id}`)).data,
    refetchInterval: (query) => (query.state.data?.status === "RUNNING" ? 2000 : false),
  });

export const useRuns = () =>
  useQuery({
    queryKey: ["ai", "runs"],
    queryFn: async () => (await api.get<RunDto[]>("/ai/runs")).data,
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === "RUNNING") ? 4000 : false,
  });

export const generateImage = async (input: ImageRequestDto) =>
  (await api.post<GeneratedImageDto>("/ai/images", input, LONG)).data;

export const generateChart = async (input: ChartRequestDto) =>
  (await api.post<GeneratedChartDto>("/ai/widgets", input, LONG)).data;
